/**
 * Home vehicle-gallery preview: thumbnail click → swap main image, plus
 * hydrates the section's top-N entries from the canonical gallery-data.json
 * (same source as /pages/vehicle-gallery) so the Home preview always
 * matches the Gallery page's order (forgeline.com display order).
 *
 * Bullet-proofing:
 * - If a gallery has no [data-vg-home-hydrate] flag, skip hydration entirely
 *   and just wire up the thumbnail-click swap on existing markup.
 * - If the JSON fetch fails or returns an empty array, leave the
 *   server-rendered fallback in place and still wire up clicks.
 * - If hydration succeeds, replace thumbnails + main image atomically, then
 *   bind click handlers on the fresh DOM.
 * - Every image URL written from JSON is filtered through isSafeImageUrl()
 *   before being assigned to an <img>.src.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.gtu-forgeline-vehicle-gallery';
  var MAIN_SELECTOR = '.gtu-forgeline-vehicle-gallery__image-row-main img.gtu-forgeline-vehicle-gallery__image';
  var THUMB_SELECTOR = '.gtu-forgeline-vehicle-gallery__image-thumbnail';
  var ACTIVE_CLASS = 'gtu-vehicle-thumb--active';

  function isSafeImageUrl(u) {
    if (typeof u !== 'string' || u.length === 0) return false;
    return /^(https:|\/\/)/.test(u);
  }

  function resizeImage(url, width) {
    if (!isSafeImageUrl(url)) return url;
    if (url.indexOf('width=') > -1) return url.replace(/width=\d+/, 'width=' + width);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function bindThumbnailClicks(gallery) {
    var mainImg = gallery.querySelector(MAIN_SELECTOR);
    var thumbs = gallery.querySelectorAll(THUMB_SELECTOR);
    if (!mainImg || thumbs.length === 0) return;

    var activeFound = false;
    for (var i = 0; i < thumbs.length; i++) {
      if (thumbs[i].classList.contains(ACTIVE_CLASS)) { activeFound = true; break; }
    }
    if (!activeFound) thumbs[0].classList.add(ACTIVE_CLASS);

    thumbs.forEach(function (thumb) {
      thumb.style.cursor = 'pointer';
      thumb.addEventListener('click', function () {
        var large = thumb.dataset.large || thumb.src;
        if (!isSafeImageUrl(large) || mainImg.src === large) return;
        mainImg.src = large;
        if (thumb.alt) mainImg.alt = thumb.alt;
        thumbs.forEach(function (t) { t.classList.remove(ACTIVE_CLASS); });
        thumb.classList.add(ACTIVE_CLASS);
      });
    });
  }

  function buildThumbnailMarkup(entries) {
    return entries.map(function (entry, idx) {
      var large = resizeImage(entry.image_url, 1200);
      var small = resizeImage(entry.image_url, 300);
      var alt = escapeAttr(entry.title || entry.image_alt || '');
      var activeAttr = idx === 0 ? ' ' + ACTIVE_CLASS : '';
      return '<img class="gtu-forgeline-vehicle-gallery__image-thumbnail' + activeAttr + '"' +
             ' data-large="' + escapeAttr(large) + '"' +
             ' src="' + escapeAttr(small) + '"' +
             ' alt="' + alt + '"' +
             ' width="auto" height="auto" />';
    }).join('');
  }

  function hydrate(gallery) {
    var url = gallery.dataset.galleryUrl;
    var maxItems = parseInt(gallery.dataset.maxItems, 10) || 8;
    if (!url) return Promise.resolve(false);

    return fetch(url, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('gallery-data.json HTTP ' + r.status);
        return r.json();
      })
      .then(function (all) {
        if (!Array.isArray(all) || all.length === 0) return false;

        var picked = [];
        for (var i = 0; i < all.length && picked.length < maxItems; i++) {
          if (all[i] && isSafeImageUrl(all[i].image_url)) picked.push(all[i]);
        }
        if (picked.length === 0) return false;

        var thumbsContainer = gallery.querySelector('[data-vg-home-thumbs]');
        var mainContainer = gallery.querySelector('[data-vg-home-main]');
        if (!thumbsContainer || !mainContainer) return false;

        thumbsContainer.innerHTML = buildThumbnailMarkup(picked);

        var first = picked[0];
        var firstLarge = resizeImage(first.image_url, 1200);
        var firstAlt = escapeAttr(first.title || first.image_alt || 'Vehicle Gallery');
        mainContainer.innerHTML =
          '<img class="gtu-forgeline-vehicle-gallery__image"' +
          ' src="' + escapeAttr(firstLarge) + '"' +
          ' alt="' + firstAlt + '"' +
          ' width="auto" height="auto" />';

        return true;
      })
      .catch(function (err) {
        if (window && window.console && console.warn) {
          console.warn('[gtu-vehicle-gallery] hydration failed, using server-rendered fallback:', err);
        }
        return false;
      });
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(function (gallery) {
      var needsHydration = gallery.hasAttribute('data-vg-home-hydrate');
      var ready = needsHydration ? hydrate(gallery) : Promise.resolve(false);
      ready.then(function () { bindThumbnailClicks(gallery); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
